import { MAX_CHALLENGES } from '../constants/settings'
import {
  GameStats,
  Solve,
  setStoredStats,
  getStoredStats,
} from './localStorage'
import { AchievementKey, getKeyedAchievement } from '../lib/achievements'

import { setStats as setFirestoreStats, updateProblemStats } from './firestore'
import { User, AuthedUser } from './user'

export const DEFAULT_STATS: GameStats = {
  winDistribution: Array.from(new Array(MAX_CHALLENGES), () => 0),
  gamesFailed: 0,
  currentStreak: 0,
  bestStreak: 0,
  totalGames: 0,
  successRate: 0,
}

// ignores lastSyncedAt
export const mergeStats = (stats1: GameStats, stats2: GameStats) => {
  const devices1 = stats1.syncedDeviceIds ?? []
  const devices2 = stats2.syncedDeviceIds ?? []
  const solves1 = stats1.solves ?? []
  const solves2 = stats2.solves ?? []
  const achievements1 = stats1.achievements ?? []
  const achievements2 = stats2.achievements ?? []
  const newDevices = [...new Set(devices1.concat(devices2))]
  const newDistribution = stats1.winDistribution.map(
    (v: number, i: number) => v + stats2.winDistribution[i]
  )
  const stats: GameStats = {
    winDistribution: newDistribution,
    gamesFailed: stats1.gamesFailed + stats2.gamesFailed,
    currentStreak: stats1.currentStreak + stats2.currentStreak,
    bestStreak: stats1.bestStreak + stats2.bestStreak,
    totalGames: stats1.totalGames + stats2.totalGames,
    successRate: 0,
    syncedDeviceIds: newDevices,
    solves: solves1.concat(solves2),
    achievements: [...new Set(achievements1.concat(achievements2))],
  }
  const successRate = getSuccessRate(stats)
  stats.successRate = successRate
  return stats
}

export const setStatsLocalAndRemote = (user: User, stats: GameStats) => {
  if (!(user instanceof AuthedUser) || !user.hasPolygonlePlus()) {
    setStoredStats(stats, null)
    return
  }
  setFirestoreStats(user.uid(), stats)
  setStoredStats(stats, user.uid())
}

export const grantAchievement = (
  gameStats: GameStats,
  achievement: AchievementKey
) => {
  const stats = structuredClone(gameStats)
  const achievements = stats.achievements ?? []
  stats.achievements = [...new Set(achievements.concat(achievement))]
  return stats
}

export const grantAndSaveAchievement = (
  user: User,
  achievement: AchievementKey
) => {
  const gameStats = user.stats()
  if ((gameStats.achievements ?? []).includes(achievement)) {
    return { achievement: null, stats: gameStats }
  }
  const updatedStats = grantAchievement(gameStats, achievement)
  setStatsLocalAndRemote(user, updatedStats)
  return {
    achievement: getKeyedAchievement(achievement),
    stats: updatedStats,
  }
}

// In stats array elements 0-5 are successes in 1-6 trys

export const addStatsForCompletedGame = (
  user: User,
  solve: Solve,
  includeGuessStats = true
) => {
  const stats = structuredClone(user.stats())

  if (!stats.solves) {
    stats.solves = []
  }
  stats.solves.push(solve)
  if (!includeGuessStats) {
    return stats
  }

  stats.totalGames += 1
  if (solve.guesses - 1 >= MAX_CHALLENGES) {
    // A fail situation
    stats.currentStreak = 0
    stats.gamesFailed += 1
  } else {
    stats.winDistribution[solve.guesses - 1] += 1
    stats.currentStreak += 1

    if (stats.bestStreak < stats.currentStreak) {
      stats.bestStreak = stats.currentStreak
    }
  }

  stats.successRate = getSuccessRate(stats)
  updateProblemStats(solve)
  return stats
}

const getSuccessRate = (gameStats: GameStats) => {
  const { totalGames, gamesFailed } = gameStats

  return Math.round(
    (100 * (totalGames - gamesFailed)) / Math.max(totalGames, 1)
  )
}

export const loadLocalStats = () => {
  return getStoredStats(null) || DEFAULT_STATS
}

export function solvesMap(stats: GameStats) {
  return new Map(
    (stats.solves ?? []).map((s) => {
      return [`${s.mode},${s.solution.toUpperCase()}`, s]
    })
  )
}
